import { yupResolver } from '@hookform/resolvers/yup';
import { usePasswordLogin } from '@sunrise-ui/api-client';
import {
  Button,
  ButtonVariants,
  InputError,
  TextInput,
  useAuthStore,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { object, string, type InferType } from 'yup';

const schema = object().shape({
  email: string().email('Must be a valid email address').required('Required'),
  password: string().required('Required'),
});

type LoginFormValues = InferType<typeof schema>;

export function EmailPasswordLoginForm() {
  const { authModal, updateAuthModal } = useAuthStore();
  const { email: storeEmail = '' } = authModal;
  const styles = useDynamicStyles(createStyles);
  const { mutate: logIn, isPending } = usePasswordLogin();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<LoginFormValues>({
    defaultValues: { email: storeEmail, password: '' },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    logIn(data);
  };

  return (
    <View style={styles.form}>
      <View>
        <Controller
          control={control}
          render={({ field: { onChange, ...field } }) => (
            <TextInput
              label="Email"
              autoCapitalize="none"
              autoComplete="email"
              inputMode="email"
              error={!!errors.email}
              {...field}
              onChange={(value) => {
                onChange(value);
                updateAuthModal({ email: value });
              }}
            />
          )}
          name="email"
        />
        <InputError fieldError={errors.email} />
      </View>
      <View>
        <Controller
          control={control}
          render={({ field }) => (
            <TextInput
              label="Password"
              autoCapitalize="none"
              secureTextEntry
              autoComplete="current-password"
              error={!!errors.password}
              {...field}
            />
          )}
          name="password"
        />
        <InputError fieldError={errors.password} />
      </View>
      <View>
        <Button
          variant={ButtonVariants.PURPLE}
          onPress={handleSubmit(onSubmit)}
          disabled={isPending}
          loading={isPending}
          style={styles.button}
        >
          Log In
        </Button>
      </View>
    </View>
  );
}

const createStyles = () =>
  StyleSheet.create({
    form: {
      width: '100%',
      maxWidth: 350,
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    button: {
      alignSelf: 'center',
    },
  });
